/**
 * Shared coupon form used by both system- and organization-scoped coupon
 * create/edit pages.
 *
 * Both scopes route through the same GraphQL create/update mutations
 * (`useCreateCouponMutation` / `useUpdateCouponMutation`). The only difference
 * is the scope key passed on create:
 *   - scope=system -> `systemOldId`
 *   - scope=org    -> `organizationId`
 *
 * The legacy REST helpers in `api/coupons.tsx` are no longer used by this form
 * (the REST endpoint was never extended to carry the new time-rule /
 * assignment_only fields), but are left in place for now.
 *
 * Field conventions (must match the backend `TimeRuleBounds`):
 *   - validDays: 7-bit weekday bitmask, bit 0 = Monday ... bit 6 = Sunday.
 *     null/0 = all days. Range 1-127 when set.
 *   - validTimeStart / validTimeEnd: minutes since local midnight (0-1439),
 *     half-open. Both null = no time rule; must be set together.
 *   - validFrom / validUntil: dates (ISO YYYY-MM-DD); from <= until.
 *   - assignmentOnly: boolean (default false).
 */

import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Stack } from '@mui/system';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import { COUPON_DURATION, COUPON_TIME_OPTION } from '../../constants';
import { history } from '../../utils';
import type { ICoupon } from '../../ts/interface/coupon.interface';
import {
  useCreateCouponMutation,
  useUpdateCouponMutation,
} from '../../hooks/coupons/mutation/V2';

import {
  MovaticFieldGroupCheck,
  MovaticFieldGroupDate,
  MovaticFieldGroupOutlinedInput,
  MovaticFieldGroupSelect,
  MovaticFieldGroupText,
} from '../Redesing/movatic-field-group';
import FormItem from '../Redesing/form-item';
import IconButtonMenu from '../Redesing/icon-button-menu';
import PageContainer from '../Redesing/page-container';
import CardView from '../Redesing/card-view';
import CustomAsyncToast from '../Redesing/custom-async-toast';
import MovaticCustomModal from '../Modal/MovaticCustomModal';

export type CouponScope = 'system' | 'org';
export type CouponFormMode = 'create' | 'edit';
export type CouponFormRenderMode = 'page' | 'modal';

export interface CouponFormProps {
  scope: CouponScope;
  mode: CouponFormMode;
  /** Existing coupon when editing. */
  initialValues?: ICoupon;
  /** Coupon id (gid) for the GraphQL mutations. */
  couponId?: string;
  /** Required when creating an org-scoped coupon. */
  organizationId?: string;
  /** Required when creating a system-scoped coupon. */
  systemOldId?: string;
  systemLoaded: boolean;
  cancelHref: string;
  successHref: string;
  saveDisabled?: boolean;
  title?: string;
  /**
   * How to render the form. `page` (default) renders the full-page CardView.
   * `modal` renders the form inside a MovaticCustomModal controlled by the
   * parent via `open` / `onClose`.
   */
  renderMode?: CouponFormRenderMode;
  /** Modal-mode only: whether the dialog is open. */
  open?: boolean;
  /** Modal-mode only: called to close the dialog (e.g. Cancel / backdrop). */
  onClose?: () => void;
  /**
   * Called after a successful create/update. When provided it takes precedence
   * over the default `history.push(successHref)` navigation — used by the modal
   * flows to close the dialog and refetch the underlying list instead of
   * navigating away.
   */
  onSuccess?: () => void;
}

interface ICouponTimeDuration {
  name: string;
  value: number;
}

// ---------------------------------------------------------------------------
// Day bitmask helpers (bit 0 = Monday ... bit 6 = Sunday)
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Convert a weekday bitmask (1-127, or null/0) into selected bit indices. */
const bitmaskToDays = (mask: number | null | undefined): number[] => {
  if (mask == null || mask === 0) return [];
  const days: number[] = [];
  for (let bit = 0; bit < 7; bit += 1) {
    if (mask & (1 << bit)) days.push(bit);
  }
  return days;
};

/** Convert selected bit indices into a weekday bitmask (null when none). */
const daysToBitmask = (days: number[]): number | null => {
  if (!days.length) return null;
  return days.reduce((mask, bit) => mask | (1 << bit), 0);
};

// ---------------------------------------------------------------------------
// Time-of-day helpers (minutes since local midnight, 0-1439)
// ---------------------------------------------------------------------------

/** Convert minutes-since-midnight into a Date (today) for the TimePicker. */
const minutesToDate = (minutes: number | null | undefined): Date | null => {
  if (minutes == null) return null;
  const d = new Date();
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
};

/** Convert a picked Date into minutes since local midnight (0-1439). */
const dateToMinutes = (date: Date | null): number | null => {
  if (!date || isNaN(date.getTime())) return null;
  return date.getHours() * 60 + date.getMinutes();
};

/** Format a Date as an ISO `YYYY-MM-DD` string in local time. */
const dateToIso = (date: Date | null): string | null => {
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Parse an ISO `YYYY-MM-DD` string into a local Date. */
const isoToDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const parsed = new Date(iso);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const CouponForm: React.FC<CouponFormProps> = ({
  scope,
  mode,
  initialValues,
  couponId,
  organizationId,
  systemOldId,
  systemLoaded,
  cancelHref,
  successHref,
  saveDisabled,
  title,
  renderMode = 'page',
  open = false,
  onClose,
  onSuccess,
}) => {
  // ---------------------------------------------------------------------------
  // Form state
  // ---------------------------------------------------------------------------
  const [name, setName] = useState<string>(initialValues?.name ?? '');
  const [code, setCode] = useState<string>(initialValues?.code ?? '');
  const [discountType, setDiscountType] = useState<string>(
    initialValues?.discount_type ?? ''
  );
  const [discount, setDiscount] = useState<number | null>(
    typeof initialValues?.discountActual === 'number'
      ? (initialValues?.discountActual as number)
      : 0
  );
  const [active, setActive] = useState<boolean>(initialValues?.active ?? false);
  const [valid, setValid] = useState<string>(
    initialValues?.duration != null ? 'time' : initialValues?.uses != null ? 'uses' : ''
  );
  const [duration, setDuration] = useState<string | null>(
    typeof initialValues?.duration === 'number' ? String(initialValues?.duration) : ''
  );
  const [uses, setUses] = useState<number | null>(
    typeof initialValues?.uses === 'number' ? (initialValues?.uses as number) : 0
  );

  // New time-rule + assignment_only state
  const [assignmentOnly, setAssignmentOnly] = useState<boolean>(
    initialValues?.assignmentOnly ?? false
  );
  const [validDays, setValidDays] = useState<number[]>(
    bitmaskToDays(initialValues?.validDays)
  );
  const [validTimeStart, setValidTimeStart] = useState<Date | null>(
    minutesToDate(initialValues?.validTimeStart)
  );
  const [validTimeEnd, setValidTimeEnd] = useState<Date | null>(
    minutesToDate(initialValues?.validTimeEnd)
  );
  const [validFrom, setValidFrom] = useState<Date | null>(
    isoToDate(initialValues?.validFrom)
  );
  const [validUntil, setValidUntil] = useState<Date | null>(
    isoToDate(initialValues?.validUntil)
  );

  const [nameValidation, setNameValidation] = useState<string | null>(null);
  const [codeValidation, setCodeValidation] = useState<string | null>(null);
  const [validValidation, setValidValidation] = useState<string | null>(null);
  const [discountValidation, setDiscountValidation] = useState<string | null>(null);
  const [durationValidation, setDurationValidation] = useState<string | null>(null);
  const [timeRuleError, setTimeRuleError] = useState<string | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  const { createCoupon, isLoading: isCreating } = useCreateCouponMutation();
  const { updateCoupon, isLoading: isUpdating } = useUpdateCouponMutation();
  const isSubmitting = isCreating || isUpdating;
  const isModal = renderMode === 'modal';

  /** Route the post-success behavior: prefer the callback, else navigate. */
  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      history.push(successHref);
    }
  };

  /** Route the cancel behavior: prefer the modal close, else navigate. */
  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      history.push(cancelHref);
    }
  };

  // ---------------------------------------------------------------------------
  // Option lists
  // ---------------------------------------------------------------------------
  const options: ICouponTimeDuration[] = COUPON_DURATION.map((d: any) => ({
    value: d.seconds,
    name: d.value,
  }));
  const timeOptions: ICouponTimeDuration[] = COUPON_TIME_OPTION.map((t: any) => ({
    value: t.seconds,
    name: t.value,
  }));

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const resetValid = () => {
    setDuration(null);
    setUses(null);
  };

  const resetDiscount = () => {
    setDiscount(null);
  };

  const isEditMode = mode === 'edit';

  /**
   * Client-side validation of the time-rule fields, mirroring the backend
   * `TimeRuleBounds`. Sets inline errors and returns true when valid.
   */
  const validateTimeRules = (): boolean => {
    let ok = true;

    // Days: bitmask must be 1-127 when set (none = all days, allowed).
    const dayMask = daysToBitmask(validDays);
    if (dayMask != null && (dayMask < 1 || dayMask > 127)) {
      ok = false;
    }

    // Time bounds: both set or both empty; each 0-1439.
    const startMinutes = dateToMinutes(validTimeStart);
    const endMinutes = dateToMinutes(validTimeEnd);
    const startSet = startMinutes != null;
    const endSet = endMinutes != null;
    if (startSet !== endSet) {
      ok = false;
      setTimeRuleError('Set both a start and end time, or leave both empty.');
    } else if (
      (startSet && (startMinutes < 0 || startMinutes > 1439)) ||
      (endSet && (endMinutes < 0 || endMinutes > 1439))
    ) {
      ok = false;
      setTimeRuleError('Times must fall within a single day.');
    } else {
      setTimeRuleError(null);
    }

    // Date range: from <= until when both set.
    if (validFrom && validUntil && validFrom.getTime() > validUntil.getTime()) {
      ok = false;
      setDateRangeError('“Valid From” must be on or before “Valid Until”.');
    } else {
      setDateRangeError(null);
    }

    return ok;
  };

  const canSubmit = () => {
    let ok = true;

    if (!name) {
      ok = false;
      setNameValidation('error');
    } else {
      setNameValidation(null);
    }

    if (!validateTimeRules()) {
      ok = false;
    }

    if (isEditMode) {
      return ok;
    }

    if (code.length > 10 || code.length === 0) {
      ok = false;
      setCodeValidation('error');
    } else {
      setCodeValidation(null);
    }
    if (discountType == null || discountType === '') {
      ok = false;
      setDiscountValidation('error');
    } else {
      setDiscountValidation(null);
    }
    if (!valid) {
      ok = false;
      setValidValidation('error');
    } else {
      setValidValidation(null);
    }
    if (!uses && !duration) {
      ok = false;
      setDurationValidation('error');
    } else {
      setDurationValidation(null);
    }
    return ok;
  };

  /** Collects the new time-rule + assignment fields shared by create & update. */
  const timeRuleFields = () => ({
    assignmentOnly,
    validDays: daysToBitmask(validDays),
    validTimeStart: dateToMinutes(validTimeStart),
    validTimeEnd: dateToMinutes(validTimeEnd),
    validFrom: dateToIso(validFrom),
    validUntil: dateToIso(validUntil),
  });

  // ---------------------------------------------------------------------------
  // Submit (single GraphQL path for both scopes)
  // ---------------------------------------------------------------------------
  const submit = async () => {
    if (!canSubmit()) return;

    if (isEditMode) {
      const result = await CustomAsyncToast({
        promise: () =>
          updateCoupon(couponId ?? '', {
            name,
            active,
            ...timeRuleFields(),
          }).then((res) => {
            if (!res.isSuccess) {
              throw new Error(res.errors[0]?.message || 'Failed to update coupon');
            }
            return true;
          }),
        successMessage: () => 'Coupon updated successfully!',
        errorMessage: 'There was an error updating the coupon.',
        loadingMessage: 'Updating coupon...',
      });
      if (result) handleSuccess();
      return;
    }

    // Create
    const discountValue =
      discountType === 'money' && discount != null ? Number(discount) * 100 : discount;

    const result = await CustomAsyncToast({
      promise: () =>
        createCoupon({
          name,
          code,
          active,
          discountType,
          discount: Number(discountValue ?? 0),
          uses: uses ?? null,
          duration: duration != null && duration !== '' ? Number(duration) : null,
          organizationId: scope === 'org' ? organizationId ?? null : null,
          systemOldId: scope === 'system' ? systemOldId ?? null : null,
          ...timeRuleFields(),
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors[0]?.message || 'Failed to create coupon');
          }
          return true;
        }),
      successMessage: () => 'Coupon added successfully!',
      errorMessage: 'There was an error adding the coupon.',
      loadingMessage: 'Adding coupon...',
    });
    if (result) handleSuccess();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const footerButtons = (
    <IconButtonMenu
      buttons={[
        {
          label: 'Cancel',
          onClick: handleCancel,
          startIcon: isModal ? <CloseIcon /> : undefined,
        },
        {
          label: 'Save',
          onClick: submit,
          startIcon: <SaveIcon />,
          disabled: saveDisabled || isSubmitting,
        },
      ]}
    />
  );

  const formBody = (
    <Stack px={isModal ? 0 : 2} pb={isModal ? 0 : 2}>
      <MovaticFieldGroupText
        label="Coupon Name"
        description={'The name of the coupon.'}
        value={name}
        hasError={nameValidation === 'error'}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setName(e.target.value);
          setNameValidation(null);
        }}
        id="name"
        name={'name'}
      />

      {!isEditMode && (
        <>
          <MovaticFieldGroupOutlinedInput
            label="Code"
            description="Please enter up to 10 characters for the user to input to apply this coupon."
            value={code}
            inputProps={{ maxLength: 10 }}
            hasError={codeValidation === 'error'}
            errorMessage={
              code.length > 10
                ? 'Code must be 10 characters or fewer.'
                : 'Code is required.'
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const next = e.target.value;
              setCode(next);
              if (next.length === 0 || next.length > 10) {
                setCodeValidation('error');
              } else {
                setCodeValidation(null);
              }
            }}
            id="code"
            name={'code'}
          />

          <MovaticFieldGroupSelect
            label="Discount Type"
            description="Please choose the discount type you would like this coupon to apply."
            name={'card_type'}
            optionNodes={[
              { value: 'percent', name: 'Percentage Discount' },
              { value: 'time', name: 'Free Time' },
              { value: 'money', name: 'Money Discount' },
            ].map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
            value={discountType}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDiscountType(e.target.value);
              resetDiscount();
            }}
            hasError={discountValidation === 'error'}
            id="discount_type"
          />

          {discountType === 'percent' && (
            <MovaticFieldGroupOutlinedInput
              label="Percent Off"
              type="number"
              addOn={'%'}
              description="Please enter the percent off you would like to apply to the coupon."
              value={discount ? discount : ''}
              inputProps={{ max: 100, min: 1 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDiscount(parseInt(e.target.value));
              }}
              id="code"
              name={'code'}
            />
          )}
          {discountType === 'money' && (
            <MovaticFieldGroupOutlinedInput
              label="Money Off"
              type="number"
              addOn={'$'}
              description="Please enter the discount you would like to apply to the coupon."
              value={discount ? discount : ''}
              inputProps={{ max: 100, min: 1, step: 0.25 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDiscount(parseInt(e.target.value));
              }}
              id="discount"
              name="discount"
            />
          )}

          {discountType === 'time' && (
            <MovaticFieldGroupSelect
              label="Free Time"
              description="Please choose an amount of of free time before a user's rental will be charged."
              optionNodes={options.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
              value={discount ? discount : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDiscount(parseInt(e.target.value));
              }}
              id="discount"
            />
          )}

          <MovaticFieldGroupSelect
            label="Valid For"
            description="Please choose the type of duration the coupon will be valid for."
            optionNodes={[
              { value: 'time', name: 'Time Based' },
              { value: 'uses', name: '# of Uses' },
            ].map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
            value={valid}
            hasError={validValidation === 'error'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValid(e.target.value);
              resetValid();
            }}
            id="discount"
          />

          {valid === 'time' && (
            <MovaticFieldGroupSelect
              label="Durations"
              description="Please choose how long the user has to use the coupon after applying it."
              optionNodes={timeOptions.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
              value={valid}
              hasError={durationValidation === 'error'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDuration(e.target.value);
                setValidValidation(null);
              }}
              id="discount"
            />
          )}

          {valid === 'uses' && (
            <MovaticFieldGroupOutlinedInput
              label="Uses"
              type="number"
              addOn={'#'}
              description="Please choose how many times the user can use this coupon."
              value={uses ? uses : ''}
              inputProps={{ max: 100, min: 1 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUses(parseInt(e.target.value));
              }}
              id="discount"
              name="discount"
            />
          )}
        </>
      )}

      <MovaticFieldGroupCheck
        label="Active?"
        description={
          isEditMode
            ? 'Would you like this coupon to be available to users?'
            : 'Make this coupon active immediately'
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setActive(e.target.checked)}
        checked={active}
        name="active"
      />

      <MovaticFieldGroupCheck
        label="Assignment only?"
        description="When enabled, this coupon can only be applied by an admin assigning it to a user (it cannot be redeemed with the code)."
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setAssignmentOnly(e.target.checked)
        }
        checked={assignmentOnly}
        name="assignment_only"
      />

      <FormItem
        label="Valid Days"
        description="Restrict the days of the week this coupon can be applied. Leave all unselected to allow every day."
        content={
          <FormControl fullWidth>
            <ToggleButtonGroup
              value={validDays}
              onChange={(_e, next: number[]) => {
                setValidDays(next);
                setTimeRuleError(null);
              }}
              aria-label="Valid days of week"
              size="small"
            >
              {DAY_LABELS.map((dayLabel, bit) => (
                <ToggleButton key={bit} value={bit} aria-label={dayLabel}>
                  {dayLabel}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </FormControl>
        }
      />

      <FormItem
        label="Valid Time Window"
        description="Restrict the time of day this coupon can be applied. Set both a start and end time, or leave both empty for no time restriction."
        content={
          <FormControl fullWidth error={timeRuleError != null}>
            <Stack direction="row" spacing={2}>
              <TimePicker
                label="Start"
                value={validTimeStart}
                onChange={(value: Date | null) => {
                  setValidTimeStart(value);
                  setTimeRuleError(null);
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    id: 'valid_time_start',
                    fullWidth: true,
                    variant: 'outlined',
                    error: timeRuleError != null,
                  },
                }}
              />
              <TimePicker
                label="End"
                value={validTimeEnd}
                onChange={(value: Date | null) => {
                  setValidTimeEnd(value);
                  setTimeRuleError(null);
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    id: 'valid_time_end',
                    fullWidth: true,
                    variant: 'outlined',
                    error: timeRuleError != null,
                  },
                }}
              />
            </Stack>
            {timeRuleError && <FormHelperText>{timeRuleError}</FormHelperText>}
          </FormControl>
        }
      />

      <MovaticFieldGroupDate
        label="Valid From"
        description="The date this coupon becomes valid. Leave empty for no start date."
        id="valid_from"
        value={validFrom}
        onChange={(value: Date | null) => {
          setValidFrom(value);
          setDateRangeError(null);
        }}
      />

      <MovaticFieldGroupDate
        label="Valid Until"
        description="The date this coupon expires. Leave empty for no end date."
        id="valid_until"
        value={validUntil}
        minDate={validFrom ?? undefined}
        hasError={dateRangeError != null}
        errorMessage={dateRangeError ?? undefined}
        onChange={(value: Date | null) => {
          setValidUntil(value);
          setDateRangeError(null);
        }}
      />
    </Stack>
  );

  if (isModal) {
    return (
      <MovaticCustomModal
        useContentDividers
        open={open}
        onClose={handleCancel}
        isLoading={isSubmitting}
        title={title ?? (isEditMode ? 'Edit Coupon' : 'Add Coupon')}
        content={formBody}
        action={footerButtons}
      />
    );
  }

  return (
    <PageContainer isLoading={!systemLoaded}>
      <CardView
        headerActions={footerButtons}
        title={title ?? (isEditMode ? 'Edit Coupon' : 'Add')}
        content={formBody}
      />
    </PageContainer>
  );
};

export default CouponForm;
